/**
 * Translate ApiError + Zod error codes into user-facing messages.
 *
 * Zod schemas in @sendsms/shared emit error codes (e.g. "email_too_short",
 * "password_required") rather than human-readable strings. That keeps the
 * schema i18n-agnostic. Translation happens here.
 */

import type { TFunction } from "i18next";
import type { UseFormSetError, FieldValues, Path } from "react-hook-form";
import { ApiError } from "./api";

/**
 * Translate a server/client field error code to its human message.
 * Codes map 1:1 to keys under `errors.validation.*` in the translation files.
 */
export function translateFieldError(t: TFunction, code: string): string {
  if (!code) return "";
  const key = `errors.validation.${code}`;
  const translated = t(key);
  return translated === key ? code : translated;
}

/**
 * Translate a top-level error code / status / message into a user-facing string.
 */
export function translateApiError(t: TFunction, err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 0) return t("errors.network");
    // Honest errors (PO diag 25-Jul): the server already identifies the
    // window and the numbers - the client finally reads them.
    if (err.code === "exceeds_daily_limit") {
      return t("errors.exceeds_daily_limit", {
        limit: Number(err.extra.daily_limit ?? 0).toLocaleString(),
        used: Number(err.extra.used_today ?? 0).toLocaleString(),
        requested: Number(err.extra.requested ?? 0).toLocaleString(),
      });
    }
    if (err.isRateLimited) {
      if (err.limitWindow === "day") return t("errors.rate_limited_day");
      return t("errors.rate_limited");
    }
    if (err.status >= 500) return t("errors.server_error");
    if (err.code) {
      const translated = t(`errors.${err.code}`);
      if (translated !== `errors.${err.code}`) return translated;
    }
    return err.message || t("errors.generic");
  }

  return t("errors.generic");
}

/**
 * Apply an ApiError's field_errors to a react-hook-form setError call.
 * Returns `true` if any field errors were mapped.
 */
export function applyFieldErrors<T extends FieldValues>(
  t: TFunction,
  err: unknown,
  setError: UseFormSetError<T>,
): boolean {
  if (!(err instanceof ApiError) || !err.hasFieldErrors) return false;

  let applied = false;
  for (const [field, codes] of Object.entries(err.fieldErrors ?? {})) {
    if (!codes?.length) continue;
    setError(field as Path<T>, {
      type: "server",
      message: translateFieldError(t, codes[0]!),
    });
    applied = true;
  }
  return applied;
}
